import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, Eye, Download, MessageSquare, TrendingUp, 
  Video, FileText, Image, ArrowLeft, RefreshCw, Trash2,
  Calendar, Users, Sparkles, Filter, ChevronDown, ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminContentStats() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [comments, setComments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [expandedContent, setExpandedContent] = useState(null);

  const getToken = () => localStorage.getItem('session_token');

  useEffect(() => {
    fetchAnalytics();
    fetchComments();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API}/api/admin/content/analytics`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API}/api/admin/comments`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('¿Estás segura de eliminar este comentario?')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        setComments(prev => prev.filter(c => c.comment_id !== commentId));
        fetchAnalytics(); // Actualizar contadores
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-4 h-4 text-pink-400" />;
      case 'pdf': return <FileText className="w-4 h-4 text-blue-400" />;
      case 'ebook': return <FileText className="w-4 h-4 text-purple-400" />;
      default: return <Image className="w-4 h-4 text-green-400" />;
    }
  };

  const getLevelBadge = (level) => {
    const colors = {
      'free': 'bg-gray-600 text-gray-200',
      'gratis': 'bg-gray-600 text-gray-200',
      'nivel_1': 'bg-blue-600 text-blue-100',
      'basico': 'bg-blue-600 text-blue-100',
      'nivel_2': 'bg-purple-600 text-purple-100',
      'intermedio': 'bg-purple-600 text-purple-100',
      'nivel_3': 'bg-pink-600 text-pink-100',
      'premium': 'bg-pink-600 text-pink-100',
      'avanzado': 'bg-pink-600 text-pink-100'
    };
    const labels = {
      'free': 'Gratis', 'gratis': 'Gratis',
      'nivel_1': 'Nivel 1', 'basico': 'Nivel 1',
      'nivel_2': 'Nivel 2', 'intermedio': 'Nivel 2',
      'nivel_3': 'Premium', 'premium': 'Premium', 'avanzado': 'Premium'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[level] || 'bg-gray-600'}`}>
        {labels[level] || level}
      </span>
    );
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      'entrenamiento': 'Entrenamiento',
      'nutricion': 'Nutrición',
      'recetas': 'Recetas',
      'ebook': 'E-Book',
      'academia': 'Academia',
      'mindset': 'Mindset'
    };
    return labels[cat] || cat;
  };

  const filteredContent = analytics?.all_content?.filter(item => {
    if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
    if (levelFilter !== 'all' && item.level !== levelFilter) return false;
    return true;
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600/20 to-purple-600/20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BarChart3 className="w-7 h-7 text-pink-500" />
                  Estadísticas de Contenido
                </h1>
                <p className="text-gray-400 text-sm mt-1">
                  Analiza el rendimiento de tu contenido
                </p>
              </div>
            </div>
            <button
              onClick={() => { fetchAnalytics(); fetchComments(); }}
              className="flex items-center gap-2 px-4 py-2 bg-pink-600 hover:bg-pink-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white/5 p-1 rounded-xl w-fit">
          {[
            { id: 'overview', label: 'Resumen', icon: TrendingUp },
            { id: 'content', label: 'Por Contenido', icon: Video },
            { id: 'comments', label: 'Comentarios', icon: MessageSquare }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id 
                  ? 'bg-pink-600 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-pink-600/20 to-pink-900/20 rounded-2xl p-6 border border-pink-500/20">
                <div className="flex items-center justify-between mb-4">
                  <Eye className="w-8 h-8 text-pink-400" />
                  <span className="text-xs text-pink-300 bg-pink-500/20 px-2 py-1 rounded-full">
                    Total
                  </span>
                </div>
                <p className="text-3xl font-bold">{analytics.summary.total_views.toLocaleString()}</p>
                <p className="text-gray-400 text-sm mt-1">Visualizaciones</p>
              </div>

              <div className="bg-gradient-to-br from-blue-600/20 to-blue-900/20 rounded-2xl p-6 border border-blue-500/20">
                <div className="flex items-center justify-between mb-4">
                  <Download className="w-8 h-8 text-blue-400" />
                  <span className="text-xs text-blue-300 bg-blue-500/20 px-2 py-1 rounded-full">
                    Total
                  </span>
                </div>
                <p className="text-3xl font-bold">{analytics.summary.total_downloads.toLocaleString()}</p>
                <p className="text-gray-400 text-sm mt-1">Descargas</p>
              </div>

              <div className="bg-gradient-to-br from-purple-600/20 to-purple-900/20 rounded-2xl p-6 border border-purple-500/20">
                <div className="flex items-center justify-between mb-4">
                  <MessageSquare className="w-8 h-8 text-purple-400" />
                  <span className="text-xs text-purple-300 bg-purple-500/20 px-2 py-1 rounded-full">
                    Total
                  </span>
                </div>
                <p className="text-3xl font-bold">{analytics.summary.total_comments.toLocaleString()}</p>
                <p className="text-gray-400 text-sm mt-1">Comentarios</p>
              </div>

              <div className="bg-gradient-to-br from-green-600/20 to-green-900/20 rounded-2xl p-6 border border-green-500/20">
                <div className="flex items-center justify-between mb-4">
                  <Sparkles className="w-8 h-8 text-green-400" />
                  <span className="text-xs text-green-300 bg-green-500/20 px-2 py-1 rounded-full">
                    Total
                  </span>
                </div>
                <p className="text-3xl font-bold">{analytics.summary.total_content}</p>
                <p className="text-gray-400 text-sm mt-1">Contenidos</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-pink-400" />
                  Últimos 30 días
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Eye className="w-5 h-5 text-pink-400" />
                      <span>Visualizaciones</span>
                    </div>
                    <span className="text-2xl font-bold text-pink-400">
                      {analytics.summary.recent_views_30d.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Download className="w-5 h-5 text-blue-400" />
                      <span>Descargas</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-400">
                      {analytics.summary.recent_downloads_30d.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Top Content */}
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Top 5 Contenidos
                </h3>
                <div className="space-y-3">
                  {analytics.top_content.slice(0, 5).map((item, index) => (
                    <div key={item.content_id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                      <span className="w-6 h-6 rounded-full bg-pink-600 flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      {getContentTypeIcon(item.content_type)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.title}</p>
                        <p className="text-xs text-gray-400">{getCategoryLabel(item.category)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-pink-400">{item.views_count}</p>
                        <p className="text-xs text-gray-400">vistas</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats by Category & Level */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-4">Por Categoría</h3>
                <div className="space-y-3">
                  {Object.entries(analytics.category_stats).map(([category, stats]) => (
                    <div key={category} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                      <div>
                        <p className="font-medium">{getCategoryLabel(category)}</p>
                        <p className="text-xs text-gray-400">{stats.count} contenidos</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-pink-400 font-bold">{stats.views}</p>
                          <p className="text-xs text-gray-400">vistas</p>
                        </div>
                        <div className="text-right">
                          <p className="text-blue-400 font-bold">{stats.downloads}</p>
                          <p className="text-xs text-gray-400">descargas</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold mb-4">Por Nivel</h3>
                <div className="space-y-3">
                  {Object.entries(analytics.level_stats).map(([level, stats]) => (
                    <div key={level} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                      <div className="flex items-center gap-3">
                        {getLevelBadge(level)}
                        <span className="text-xs text-gray-400">{stats.count} contenidos</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-pink-400 font-bold">{stats.views}</p>
                          <p className="text-xs text-gray-400">vistas</p>
                        </div>
                        <div className="text-right">
                          <p className="text-blue-400 font-bold">{stats.downloads}</p>
                          <p className="text-xs text-gray-400">descargas</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && analytics && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center bg-white/5 p-4 rounded-xl">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-400">Filtros:</span>
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">Todas las categorías</option>
                <option value="entrenamiento">Entrenamiento</option>
                <option value="nutricion">Nutrición</option>
                <option value="recetas">Recetas</option>
                <option value="ebook">E-Books</option>
                <option value="academia">Academia</option>
              </select>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">Todos los niveles</option>
                <option value="free">Gratis</option>
                <option value="nivel_1">Nivel 1</option>
                <option value="nivel_2">Nivel 2</option>
                <option value="premium">Premium</option>
              </select>
              <span className="text-sm text-gray-400 ml-auto">
                {filteredContent.length} contenidos
              </span>
            </div>

            {/* Content List */}
            <div className="space-y-3">
              {filteredContent.map(item => (
                <div 
                  key={item.content_id}
                  className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
                >
                  <div 
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setExpandedContent(expandedContent === item.content_id ? null : item.content_id)}
                  >
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg bg-white/10 overflow-hidden flex-shrink-0">
                      {item.cover_image ? (
                        <img 
                          src={item.cover_image} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {getContentTypeIcon(item.content_type)}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getContentTypeIcon(item.content_type)}
                        <h4 className="font-medium truncate">{item.title}</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        {getLevelBadge(item.level)}
                        <span className="text-xs text-gray-400">{getCategoryLabel(item.category)}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-lg font-bold text-pink-400">{item.views_count}</p>
                        <p className="text-xs text-gray-400">Vistas</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-400">{item.downloads_count}</p>
                        <p className="text-xs text-gray-400">Descargas</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-purple-400">{item.comments_count}</p>
                        <p className="text-xs text-gray-400">Comentarios</p>
                      </div>
                      {expandedContent === item.content_id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedContent === item.content_id && (
                    <div className="px-4 pb-4 pt-2 border-t border-white/10 bg-white/5">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Usuarios únicos</p>
                          <p className="font-bold flex items-center gap-1">
                            <Users className="w-4 h-4 text-green-400" />
                            {item.unique_viewers}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Promedio vistas/usuario</p>
                          <p className="font-bold">
                            {item.unique_viewers > 0 ? (item.views_count / item.unique_viewers).toFixed(1) : '0'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Fecha de creación</p>
                          <p className="text-sm">{formatDate(item.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">ID</p>
                          <p className="text-xs font-mono text-gray-500">{item.content_id}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div className="space-y-6">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                  Todos los comentarios
                </h3>
                <span className="text-sm text-gray-400">{comments.length} comentarios</span>
              </div>
            </div>

            {comments.length === 0 ? (
              <div className="text-center py-12 bg-white/5 rounded-xl">
                <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No hay comentarios aún</p>
                <p className="text-sm text-gray-500 mt-1">Los comentarios de los usuarios aparecerán aquí</p>
              </div>
            ) : (
              <div className="space-y-3">
                {comments.map(comment => (
                  <div key={comment.comment_id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-pink-400">{comment.user_name}</span>
                          <span className="text-gray-500">•</span>
                          <span className="text-xs text-gray-400">{comment.user_email}</span>
                        </div>
                        <p className="text-gray-300 mb-3">{comment.text}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            {getContentTypeIcon(comment.content_type)}
                            {comment.content_title}
                          </span>
                          <span>{formatDate(comment.created_at)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteComment(comment.comment_id)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Eliminar comentario"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
